/**
 * Gráficos do painel — SVG escrito à mão.
 *
 * Optei por não usar uma biblioteca de charts porque as especificações de marca
 * que este projeto segue (extremidades arredondadas de 4px ancoradas na linha de
 * base, traço de 2px, folga de 2px entre preenchimentos, rótulos diretos
 * seletivos) são exatamente as que bibliotecas prontas dificultam. Os quatro
 * gráficos aqui são simples o bastante para o controle direto valer mais que a
 * dependência — e o bundle fica menor, o que importa no 4G do consultor em campo.
 *
 * Paleta validada com o script de checagem (banda de luminosidade, piso de
 * croma, separação para daltonismo e contraste) nos modos claro e escuro.
 *
 * Todos usam viewBox e largura 100%: escalam sem precisar medir o contêiner.
 */

import { useId, useState } from 'react';
import { numero, percentual } from '../../lib/format.ts';

const SERIES = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/** Cor da série na posição fixa. A 6ª em diante vira "Outros" — nunca uma cor gerada. */
export const corSerie = (i: number): string => SERIES[i] ?? 'var(--chart-other)';

// ─────────────────────────── Série temporal ───────────────────────────

export interface PontoTempo {
  rotulo: string;
  valor: number;
}

/**
 * Área + linha para adesões ao longo do tempo.
 *
 * Série única, então não leva legenda — o título do card já a nomeia. O último
 * ponto recebe rótulo direto; os demais aparecem no toque.
 */
export function AreaTempo({
  dados,
  altura = 180,
  sufixo = '',
}: {
  dados: PontoTempo[];
  altura?: number;
  sufixo?: string;
}) {
  const gradId = useId();
  const [ativo, setAtivo] = useState<number | null>(null);

  if (dados.length === 0) return <SemDados altura={altura} />;

  const L = 8;
  const R = 8;
  const T = 18;
  const B = 26;
  const W = 340;
  const H = altura;
  const areaW = W - L - R;
  const areaH = H - T - B;

  const max = Math.max(...dados.map((d) => d.valor), 1);
  const x = (i: number) => L + (dados.length === 1 ? areaW / 2 : (i / (dados.length - 1)) * areaW);
  const y = (v: number) => T + areaH - (v / max) * areaH;

  const linha = dados.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(d.valor)}`).join(' ');
  const area = `${linha} L ${x(dados.length - 1)} ${T + areaH} L ${x(0)} ${T + areaH} Z`;

  const ultimo = dados.length - 1;
  const alvo = ativo ?? ultimo;
  const passo = areaW / Math.max(dados.length, 1);

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={`Evolução: ${dados.map((d) => `${d.rotulo} ${d.valor}`).join(', ')}`}
        style={{ display: 'block', touchAction: 'pan-y' }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-1)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--chart-1)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grade recessiva: três linhas, sem moldura. */}
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={L}
            x2={W - R}
            y1={T + areaH * f}
            y2={T + areaH * f}
            stroke="var(--chart-grid)"
            strokeWidth="1"
          />
        ))}

        <path d={area} fill={`url(#${gradId})`} />
        <path
          d={linha}
          fill="none"
          stroke="var(--chart-1)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Marcador do ponto em foco, com anel da superfície para destacar. */}
        <circle
          cx={x(alvo)}
          cy={y(dados[alvo]!.valor)}
          r="5"
          fill="var(--chart-1)"
          stroke="var(--chart-surface)"
          strokeWidth="2"
        />

        {/* Rótulo direto — só no ponto em foco, nunca em todos. */}
        <text
          x={Math.min(Math.max(x(alvo), 24), W - 24)}
          y={Math.max(y(dados[alvo]!.valor) - 10, 12)}
          textAnchor="middle"
          fill="var(--fg)"
          fontSize="13"
          fontWeight="700"
        >
          {numero(dados[alvo]!.valor)}
          {sufixo}
        </text>

        {/* Primeiro e último rótulos do eixo: no mobile não cabe mais que isso. */}
        <text x={L} y={H - 8} fill="var(--faint)" fontSize="11">
          {dados[0]!.rotulo}
        </text>
        {dados.length > 1 && (
          <text x={W - R} y={H - 8} textAnchor="end" fill="var(--faint)" fontSize="11">
            {dados[ultimo]!.rotulo}
          </text>
        )}

        {/* Alvos de toque largos, invisíveis — bem maiores que o marcador. */}
        {dados.map((d, i) => (
          <rect
            key={d.rotulo}
            x={x(i) - passo / 2}
            y={0}
            width={passo}
            height={H}
            fill="transparent"
            onPointerEnter={() => setAtivo(i)}
            onPointerDown={() => setAtivo(i)}
            onPointerLeave={() => setAtivo(null)}
          >
            <title>{`${d.rotulo}: ${numero(d.valor)}${sufixo}`}</title>
          </rect>
        ))}
      </svg>
    </figure>
  );
}

// ─────────────────────────── Ranking horizontal ───────────────────────────

export interface ItemRanking {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}

/**
 * Barras horizontais para ranking.
 *
 * Horizontal, e não vertical, porque nome de consultor não cabe em rótulo
 * vertical no celular — e girar texto é pior que girar a barra.
 */
/** Rótulos que não representam nada e só ocupam espaço na leitura. */
const SEM_INFORMACAO = new Set(['não informado', 'nao informado', 'sem cooperativa', 'sem consultor', '—', '']);

export function RankingBarras({
  itens,
  sufixo = '',
  formatar,
  rotuloColuna = 'Nome',
  rotuloMedida = 'Valor',
}: {
  itens: ItemRanking[];
  sufixo?: string;
  /** Troca a formatação do valor — usado quando a medida é dinheiro, não contagem. */
  formatar?: (v: number) => string;
  rotuloColuna?: string;
  rotuloMedida?: string;
}) {
  /**
   * Ordenação por valor, decrescente, por padrão.
   *
   * É a única ordem que responde a pergunta que o card faz. Ordenar por
   * quantidade num gráfico de ticket colocava o maior ticket na quarta linha,
   * que é onde ninguém olha.
   */
  const [ordem, setOrdem] = useState<{ campo: 'rotulo' | 'valor'; desc: boolean }>({
    campo: 'valor',
    desc: true,
  });

  const alternar = (campo: 'rotulo' | 'valor') =>
    setOrdem((o) =>
      // Clicar de novo na mesma coluna inverte; trocar de coluna começa na
      // ordem mais útil para ela — nome de A a Z, número do maior para o menor.
      o.campo === campo ? { campo, desc: !o.desc } : { campo, desc: campo === 'valor' },
    );

  // Linhas sem informação ou zeradas não dizem nada e empurram o resto para
  // baixo. Ficam de fora da leitura; o dado bruto segue no espelho.
  const visiveis = itens.filter(
    (i) => i.valor > 0 && !SEM_INFORMACAO.has(i.rotulo.trim().toLowerCase()),
  );

  const ordenados = [...visiveis].sort((a, b) => {
    const r =
      ordem.campo === 'valor'
        ? a.valor - b.valor
        : a.rotulo.localeCompare(b.rotulo, 'pt-BR', { sensitivity: 'base' });
    return ordem.desc ? -r : r;
  });

  if (ordenados.length === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted)]">Sem dados no período.</p>;
  }

  const max = Math.max(...ordenados.map((i) => i.valor), 1);

  return (
    <>
      <div className="mb-2.5 flex items-center gap-3 border-b border-[var(--border)] pb-2">
        <span className="w-5 shrink-0" />
        <Cabecalho
          rotulo={rotuloColuna}
          ativo={ordem.campo === 'rotulo'}
          desc={ordem.desc}
          onClick={() => alternar('rotulo')}
          className="min-w-0 flex-1 truncate"
        />
        <Cabecalho
          rotulo={rotuloMedida}
          ativo={ordem.campo === 'valor'}
          desc={ordem.desc}
          onClick={() => alternar('valor')}
          className="shrink-0"
        />
      </div>

      <ol className="flex flex-col gap-3 m-0 p-0 list-none">
        {ordenados.map((item, i) => (
          <li key={item.rotulo} className="flex items-center gap-3">
          <span className="w-5 shrink-0 text-xs font-bold tabular-nums text-[var(--faint)]">
            {i + 1}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className="truncate text-sm font-semibold text-[var(--fg)]">{item.rotulo}</span>
              {/* Rótulo direto: o valor sempre visível, sem depender de toque. */}
              <span className="num shrink-0 text-sm font-medium text-[var(--fg)]">
                {formatar ? formatar(item.valor) : `${numero(item.valor)}${sufixo}`}
              </span>
            </div>

            <div className="h-1.5 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-200)]">
              <div
                className="h-full rounded-[var(--r-full)]"
                style={{
                  width: `${Math.max((item.valor / max) * 100, 2)}%`,
                  background: item.destaque ? 'var(--brand)' : corSerie(0),
                  opacity: item.destaque ? 1 : 0.75,
                }}
              />
            </div>
          </div>
          </li>
        ))}
      </ol>
    </>
  );
}

/** Cabeçalho clicável de coluna, com a seta indicando a direção atual. */
function Cabecalho({
  rotulo,
  ativo,
  desc,
  onClick,
  className,
}: {
  rotulo: string;
  ativo: boolean;
  desc: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-sort={ativo ? (desc ? 'descending' : 'ascending') : 'none'}
      className={
        'anel-foco inline-flex items-center gap-1 rounded-[var(--r-sm)] text-[11px] font-medium uppercase tracking-wide transition-colors ' +
        (ativo ? 'text-[var(--fg)]' : 'text-[var(--faint)] hover:text-[var(--muted)]') +
        (className ? ' ' + className : '')
      }
    >
      {rotulo}
      <span aria-hidden className={ativo ? 'opacity-100' : 'opacity-30'}>
        {ativo && !desc ? '↑' : '↓'}
      </span>
    </button>
  );
}

// ─────────────────────────── Mix (rosca) ───────────────────────────

export interface FatiaMix {
  rotulo: string;
  valor: number;
}

/**
 * Rosca para composição. Legenda sempre presente e com valor ao lado — a
 * identidade nunca depende só da cor, que é o que quebra para quem tem
 * daltonismo.
 */
export function RoscaMix({ fatias }: { fatias: FatiaMix[] }) {
  const [ativo, setAtivo] = useState<number | null>(null);

  const total = fatias
    .filter((f) => f.valor > 0 && !SEM_INFORMACAO.has(f.rotulo.trim().toLowerCase()))
    .reduce((s, f) => s + f.valor, 0);
  if (total === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted)]">Sem dados no período.</p>;
  }

  /**
   * A partir da 6ª fatia agrupa em "Outros", em vez de inventar cor — a paleta
   * tem cinco matizes validados e uma sexta cor gerada quebraria a separação
   * para daltonismo.
   *
   * Mas "Outros" sem dizer o que contém é uma pergunta sem resposta na tela.
   * O nome da fatia passa a listar os itens que ela agrega.
   */
  const relevantes = fatias.filter(
    (f) => f.valor > 0 && !SEM_INFORMACAO.has(f.rotulo.trim().toLowerCase()),
  );
  const ordenadas = [...relevantes].sort((a, b) => b.valor - a.valor);
  const visiveis = ordenadas.slice(0, 5);
  const agrupadas = ordenadas.slice(5);
  const resto = agrupadas.reduce((s, f) => s + f.valor, 0);
  const nomeOutros =
    agrupadas.length > 0 ? `Outros (${agrupadas.map((f) => f.rotulo).join(', ')})` : 'Outros';
  const dados = resto > 0 ? [...visiveis, { rotulo: nomeOutros, valor: resto }] : visiveis;

  const raio = 54;
  const circ = 2 * Math.PI * raio;
  let acumulado = 0;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <svg viewBox="0 0 140 140" width="140" height="140" role="img" className="shrink-0"
        aria-label={dados.map((d) => `${d.rotulo}: ${d.valor}`).join(', ')}>
        <g transform="rotate(-90 70 70)">
          {dados.map((f, i) => {
            const fracao = f.valor / total;
            // Folga de 2px entre fatias: sem ela, cores vizinhas se fundem.
            const comprimento = Math.max(fracao * circ - 2, 0);
            const offset = -acumulado * circ;
            acumulado += fracao;
            return (
              <circle
                key={f.rotulo}
                cx="70"
                cy="70"
                r={raio}
                fill="none"
                stroke={corSerie(i)}
                strokeWidth={ativo === i ? 22 : 18}
                strokeDasharray={`${comprimento} ${circ}`}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-width 140ms ease' }}
                onPointerEnter={() => setAtivo(i)}
                onPointerDown={() => setAtivo(i)}
                onPointerLeave={() => setAtivo(null)}
              >
                <title>{`${f.rotulo}: ${numero(f.valor)} (${percentual(fracao * 100, 1)})`}</title>
              </circle>
            );
          })}
        </g>
        <text x="70" y="66" textAnchor="middle" fill="var(--fg)" fontSize="22" fontWeight="800">
          {numero(ativo === null ? total : dados[ativo]!.valor)}
        </text>
        <text x="70" y="82" textAnchor="middle" fill="var(--muted)" fontSize="10" fontWeight="600">
          {ativo === null ? 'total' : dados[ativo]!.rotulo}
        </text>
      </svg>

      <ul className="w-full flex flex-col gap-2 m-0 p-0 list-none">
        {dados.map((f, i) => (
          <li key={f.rotulo} className="flex items-center gap-2.5 text-sm">
            <span
              aria-hidden
              className="size-3 rounded-full shrink-0"
              style={{ background: corSerie(i) }}
            />
            <span className="truncate text-[var(--muted)]">{f.rotulo}</span>
            <span className="ml-auto shrink-0 font-bold tabular-nums text-[var(--fg)]">
              {numero(f.valor)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────── Comparativo de duas séries ───────────────────────────

export interface SerieBarras {
  chave: string;
  rotulo: string;
  cor: string;
}

export interface LinhaPeriodo {
  rotulo: string;
  valores: Record<string, number>;
}

/**
 * Várias séries por período — recebido, a receber, inadimplência.
 *
 * Barras HORIZONTAIS empilhadas por linha, e não colunas agrupadas: doze meses
 * × três colunas viram trinta e seis marcas espremidas num celular, com rótulo
 * de mês girado. Em linhas, cada período tem seu rótulo legível e as barras
 * ficam uma sobre a outra, fáceis de comparar.
 *
 * Meses inteiramente zerados são omitidos: um consultor que começou em maio não
 * precisa de quatro linhas vazias antes da primeira venda. Quantos foram
 * omitidos aparece no rodapé, para a ausência ser informação e não mistério.
 *
 * Legenda sempre presente, e o valor escrito ao lado de cada barra — identidade
 * nunca depende só da cor.
 */
export function BarrasPorPeriodo({
  linhas,
  series,
  formatar,
}: {
  linhas: LinhaPeriodo[];
  series: SerieBarras[];
  formatar: (v: number) => string;
}) {
  const comValor = linhas.filter((l) => series.some((s) => (l.valores[s.chave] ?? 0) > 0));
  const omitidos = linhas.length - comValor.length;

  if (comValor.length === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted)]">Sem dados no período.</p>;
  }

  const max = Math.max(
    ...comValor.flatMap((l) => series.map((s) => l.valores[s.chave] ?? 0)),
    1,
  );

  return (
    <div>
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {comValor.map((l) => (
          <li key={l.rotulo}>
            <p className="mb-1.5 text-[13px] font-medium text-[var(--fg)]">{l.rotulo}</p>
            <div className="flex flex-col gap-1">
              {series.map((s) => (
                <Barra
                  key={s.chave}
                  valor={l.valores[s.chave] ?? 0}
                  max={max}
                  cor={s.cor}
                  texto={formatar(l.valores[s.chave] ?? 0)}
                  nome={s.rotulo}
                />
              ))}
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--border)] pt-3">
        {series.map((s) => (
          <ItemLegenda key={s.chave} cor={s.cor} rotulo={s.rotulo} />
        ))}
        {omitidos > 0 && (
          <span className="ml-auto text-xs text-[var(--faint)]">
            {omitidos} {omitidos === 1 ? 'mês sem movimento' : 'meses sem movimento'} omitido
            {omitidos === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </div>
  );
}

function Barra({
  valor,
  max,
  cor,
  texto,
  nome,
}: {
  valor: number;
  max: number;
  cor: string;
  texto: string;
  nome: string;
}) {
  return (
    <div className="flex items-center gap-2" title={`${nome}: ${texto}`}>
      <div className="h-4 min-w-0 flex-1 overflow-hidden rounded-[var(--r-sm)] bg-[var(--gray-100)]">
        <div
          className="h-full rounded-[var(--r-sm)]"
          style={{ width: `${Math.max((valor / max) * 100, valor > 0 ? 2 : 0)}%`, background: cor }}
        />
      </div>
      <span className="num w-16 shrink-0 truncate text-right text-xs text-[var(--muted)] sm:w-24">
        {texto}
      </span>
    </div>
  );
}

function ItemLegenda({ cor, rotulo }: { cor: string; rotulo: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-[var(--muted)]">
      <span aria-hidden className="size-2.5 rounded-sm" style={{ background: cor }} />
      {rotulo}
    </span>
  );
}

// ─────────────────────────── Evolução da base ───────────────────────────

export interface MesBase {
  rotulo: string;
  baseAtiva: number;
  adesoes: number;
  inativacoes: number;
}

/**
 * Movimento da carteira mês a mês, em colunas divergentes: adesões sobem da
 * linha de base, saídas descem. Mês de crescimento é o que tem mais verde
 * acima que vermelho abaixo — leitura de relance, sem números.
 *
 * Altura fixa, qualquer que seja a quantidade de meses. A versão anterior era
 * uma lista com três linhas por mês: doze meses davam quase dois metros de
 * rolagem no celular. Os números ficam no cabeçalho, para o mês escolhido com
 * um toque (o último, de saída) — base ao fim do mês, entradas, saídas e o
 * saldo, que é a pergunta real ("cresci ou encolhi?").
 */
export function EvolucaoBase({ meses }: { meses: MesBase[] }) {
  const [escolhido, setEscolhido] = useState<number | null>(null);

  if (meses.length === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted)]">Sem dados no período.</p>;
  }

  const atual = Math.min(escolhido ?? meses.length - 1, meses.length - 1);
  const m = meses[atual]!;
  const saldo = m.adesoes - m.inativacoes;
  const max = Math.max(...meses.flatMap((x) => [x.adesoes, x.inativacoes]), 1);

  // Geometria em unidades do viewBox; o SVG escala para a largura do card.
  const COL = 30;
  const MEIA = 64;
  const eixo = MEIA + 4;
  const largura = meses.length * COL;
  const altura = eixo + MEIA + 20;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]">
        <span className="font-medium text-[var(--fg)]">{m.rotulo}</span>
        <span className="num" style={{ color: 'var(--success)' }}>
          +{numero(m.adesoes)} adesões
        </span>
        <span className="num" style={{ color: 'var(--danger)' }}>
          −{numero(m.inativacoes)} saídas
        </span>
        <span className="ml-auto text-xs text-[var(--muted)]">
          base <strong className="num text-[13px] text-[var(--fg)]">{numero(m.baseAtiva)}</strong>
          {saldo !== 0 && (
            <span
              className="num ml-1"
              style={{ color: saldo > 0 ? 'var(--success)' : 'var(--danger)' }}
            >
              ({saldo > 0 ? '+' : '−'}
              {numero(Math.abs(saldo))})
            </span>
          )}
        </span>
      </div>

      <svg
        viewBox={`0 0 ${largura} ${altura}`}
        width="100%"
        style={{ display: 'block', maxHeight: 220 }}
        role="img"
        aria-label={`Adesões e saídas por mês, de ${meses[0]!.rotulo} a ${meses.at(-1)!.rotulo}`}
      >
        {meses.map((x, k) => {
          const sobe = (x.adesoes / max) * MEIA;
          const desce = (x.inativacoes / max) * MEIA;
          const sel = k === atual;
          return (
            <g
              key={x.rotulo}
              onClick={() => setEscolhido(k)}
              style={{ cursor: 'pointer' }}
              opacity={sel ? 1 : 0.55}
            >
              {/* Alvo de toque: a coluna inteira, não só a barra. */}
              <rect
                x={k * COL}
                y={0}
                width={COL}
                height={altura}
                fill={sel ? 'var(--gray-100)' : 'transparent'}
                rx={4}
              />
              {sobe > 0 && (
                <rect x={k * COL + 7} y={eixo - sobe} width={COL - 14} height={sobe} rx={3} fill="var(--success)" />
              )}
              {desce > 0 && (
                <rect x={k * COL + 7} y={eixo} width={COL - 14} height={desce} rx={3} fill="var(--danger)" />
              )}
              <text
                x={k * COL + COL / 2}
                y={altura - 5}
                textAnchor="middle"
                fontSize={11}
                fontWeight={sel ? 600 : 400}
                fill={sel ? 'var(--fg)' : 'var(--faint)'}
              >
                {x.rotulo.split('/')[0]}
              </text>
            </g>
          );
        })}
        <line x1={0} x2={largura} y1={eixo} y2={eixo} stroke="var(--chart-grid)" strokeWidth={1} />
      </svg>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--border)] pt-3">
        <ItemLegenda cor="var(--success)" rotulo="Adesões" />
        <ItemLegenda cor="var(--danger)" rotulo="Saídas" />
        <span className="ml-auto text-xs text-[var(--faint)]">Toque num mês para ver os números</span>
      </div>
    </div>
  );
}

// ─────────────────────────── Sparkline ───────────────────────────

/**
 * Linha mínima de tendência para os cartões de indicador. Sem eixo, sem
 * rótulo: só a forma — "vem subindo ou caindo?". O número exato fica no
 * cartão; a variação, escrita ao lado por quem usa.
 */
export function Sparkline({
  valores,
  cor = 'var(--muted)',
  area,
  altura = 24,
}: {
  valores: number[];
  cor?: string;
  /** Preenche abaixo da linha com um degradê do tom — usado no rodapé dos cartões. */
  area?: boolean;
  altura?: number;
}) {
  const id = useId();
  if (valores.length < 2) return null;
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const faixa = max - min || 1;
  // Margem de 2 unidades em cima e embaixo para o traço não ser cortado.
  const xy = valores.map(
    (v, i) => [(i / (valores.length - 1)) * 100, 22 - ((v - min) / faixa) * 20] as const,
  );
  const pontos = xy.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      width="100%"
      height={altura}
      aria-hidden
      style={{ display: 'block' }}
    >
      {area && (
        <>
          <defs>
            <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={cor} stopOpacity={0.22} />
              <stop offset="100%" stopColor={cor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <polygon points={`0,24 ${pontos} 100,24`} fill={`url(#${id})`} />
        </>
      )}
      <polyline
        points={pontos}
        fill="none"
        stroke={cor}
        strokeWidth={area ? 2 : 1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ─────────────────────────── Auxiliar ───────────────────────────

function SemDados({ altura }: { altura: number }) {
  return (
    <div
      className="grid place-items-center text-sm text-[var(--muted)]"
      style={{ height: altura }}
    >
      Sem dados no período.
    </div>
  );
}

// ─────────────────────────── Taxas mês a mês ───────────────────────────

export interface SerieTaxa {
  chave: string;
  rotulo: string;
  cor: string;
}

export interface MesTaxa {
  rotulo: string;
  /** Percentuais de 0 a 100; nulo quando o mês não tem base para a conta. */
  valores: Record<string, number | null>;
}

/**
 * Duas ou três taxas no tempo, em linhas sobre a mesma escala de 0 a 100%.
 *
 * Escala fixa, e não ajustada ao máximo: taxa de inadimplência de 8% num eixo
 * que termina em 9% parece catástrofe. Os números do mês escolhido (o último,
 * de saída) ficam no cabeçalho; tocar numa coluna troca o mês.
 */
export function LinhasTaxa({ meses, series }: { meses: MesTaxa[]; series: SerieTaxa[] }) {
  const [escolhido, setEscolhido] = useState<number | null>(null);

  if (meses.length === 0) {
    return <p className="py-8 text-center text-sm text-[var(--muted)]">Sem dados no período.</p>;
  }

  const atual = Math.min(escolhido ?? meses.length - 1, meses.length - 1);
  const m = meses[atual]!;

  const COL = 44;
  const T = 10;
  const AREA = 120;
  const largura = meses.length * COL;
  const altura = T + AREA + 22;
  const x = (k: number) => k * COL + COL / 2;
  const y = (v: number) => T + AREA - (Math.min(v, 100) / 100) * AREA;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]">
        <span className="font-medium text-[var(--fg)]">{m.rotulo}</span>
        {series.map((s) => (
          <span key={s.chave} className="num" style={{ color: s.cor }}>
            {m.valores[s.chave] === null ? '—' : percentual(m.valores[s.chave], 1)}{' '}
            <span className="text-xs text-[var(--muted)]">{s.rotulo.toLowerCase()}</span>
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${largura} ${altura}`}
        width="100%"
        style={{ display: 'block', maxHeight: 200 }}
        role="img"
        aria-label={`${series.map((s) => s.rotulo).join(', ')} por mês`}
      >
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={0}
            x2={largura}
            y1={T + AREA * f}
            y2={T + AREA * f}
            stroke="var(--chart-grid)"
            strokeWidth={1}
          />
        ))}

        {meses.map((x0, k) => (
          <g key={x0.rotulo} onClick={() => setEscolhido(k)} style={{ cursor: 'pointer' }}>
            <rect
              x={k * COL}
              y={0}
              width={COL}
              height={altura}
              rx={4}
              fill={k === atual ? 'var(--gray-100)' : 'transparent'}
            />
            <text
              x={x(k)}
              y={altura - 5}
              textAnchor="middle"
              fontSize={11}
              fontWeight={k === atual ? 600 : 400}
              fill={k === atual ? 'var(--fg)' : 'var(--faint)'}
            >
              {x0.rotulo.split('/')[0]}
            </text>
          </g>
        ))}

        {series.map((s) => {
          // Um mês sem base quebra a linha em vez de despencar a zero.
          let d = '';
          let caneta = false;
          meses.forEach((mm, k) => {
            const v = mm.valores[s.chave];
            if (v === null || v === undefined) {
              caneta = false;
              return;
            }
            d += `${caneta ? 'L' : 'M'} ${x(k)} ${y(v)} `;
            caneta = true;
          });
          return (
            <g key={s.chave} pointerEvents="none">
              <path
                d={d}
                fill="none"
                stroke={s.cor}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {meses.map((mm, k) => {
                const v = mm.valores[s.chave];
                if (v === null || v === undefined) return null;
                return (
                  <circle
                    key={k}
                    cx={x(k)}
                    cy={y(v)}
                    r={k === atual ? 4.5 : 3}
                    fill={s.cor}
                    stroke="var(--chart-surface)"
                    strokeWidth={k === atual ? 2 : 1}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--border)] pt-3">
        {series.map((s) => (
          <ItemLegenda key={s.chave} cor={s.cor} rotulo={s.rotulo} />
        ))}
        <span className="ml-auto text-xs text-[var(--faint)]">Toque num mês para ver os números</span>
      </div>
    </div>
  );
}
